import React, { useEffect, useState, useRef } from 'react';
import { SideNavbar } from '../navbar/sideNavbar';
import NotificationBell from '../common/notificationBell';
import { registerPushSubscription } from '../../utils/pushUtils';
import { useUser } from '../../context/userContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAgentDetailsByAgentId } from '../../api/userApi';
import './MainLayout.css';

const MainLayout = ({ children }) => {
    const { user, setUser } = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    const [impersonating, setImpersonating] = useState(false);
    const [adminUsername, setAdminUsername] = useState('');
    const [profileIncomplete, setProfileIncomplete] = useState(false);
    // Viewer-only users have no Dashboard access — top-bar logo routes
    // them to their profile, and the notification bell (showing sales
    // tasks / commission alerts) is hidden entirely.
    const userRoles = user?.roles || [];
    const isViewerOnly = userRoles.length > 0 && userRoles.every(r => r === 'Viewer');
    const homePath = isViewerOnly ? '/user/profile' : '/dashboard';

    useEffect(() => {
        registerPushSubscription();
        const from = localStorage.getItem('ImpersonatingFrom');
        if (from) {
            setImpersonating(true);
            setAdminUsername(from);
        }

        // Check if profile is complete
        if (user?.userId) {
            getAgentDetailsByAgentId(user.userId).then(res => {
                const d = res.data;
                // Required for a complete profile: personal name + IC + bank details.
                // Company/tax/address fields are OPTIONAL ("if applicable"), so an
                // empty billingName/tax/address must NOT trigger the warning.
                const missing = !d?.firstName || !d?.lastName || !d?.icNumber || !d?.bankName || !d?.bankAccountNumber;
                setProfileIncomplete(missing);
                if (missing && location.pathname !== '/user/profile') {
                    navigate('/user/profile');
                }
            }).catch(() => {});
        }
    }, [user?.userId]);

    // Pull-to-refresh for the iOS/Android home-screen (standalone) app, where the
    // browser's native pull-to-refresh isn't available. Pull down from the top of
    // the scroll area past the threshold to reload.
    const bodyRef = useRef(null);
    const pull = useRef({ startY: 0, active: false });
    const [pullDist, setPullDist] = useState(0);
    const REFRESH_THRESHOLD = 60;

    const onTouchStart = (e) => {
        const el = bodyRef.current;
        if (el && el.scrollTop <= 0) {
            pull.current = { startY: e.touches[0].clientY, active: true };
        } else {
            pull.current.active = false;
        }
    };
    const onTouchMove = (e) => {
        if (!pull.current.active) return;
        const dy = e.touches[0].clientY - pull.current.startY;
        if (dy > 0) setPullDist(Math.min(dy * 0.5, 80));
        else setPullDist(0);
    };
    const onTouchEnd = () => {
        if (pull.current.active && pullDist >= REFRESH_THRESHOLD) {
            setPullDist(REFRESH_THRESHOLD);
            window.location.reload();
        } else {
            setPullDist(0);
        }
        pull.current.active = false;
    };

    const handleBackToAdmin = () => {
        const adminToken = localStorage.getItem('AdminToken');
        const adminUserId = localStorage.getItem('AdminUserId');

        if (!adminToken || !adminUserId) return;

        // Restore admin session
        localStorage.setItem('Token', adminToken);
        localStorage.setItem('UserId', adminUserId);
        localStorage.removeItem('AdminToken');
        localStorage.removeItem('AdminUserId');
        localStorage.removeItem('ImpersonatingFrom');

        // Force reload to re-authenticate as admin
        window.location.href = '/dashboard';
    };

    return (
        <div className="main-layout">
            <SideNavbar />
            <div className="main-content">
                {impersonating && (
                    <div className="impersonate-bar">
                        <span>You are viewing as another user</span>
                        <button onClick={handleBackToAdmin}>
                            Back to {adminUsername}
                        </button>
                    </div>
                )}
                <div className="main-header">
                    <div onClick={() => navigate(homePath)} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <img src="/nexwaveLogo.png" alt="Nexwave" style={{ height: 28, marginRight: 8 }} />
                        <span style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.9rem', color: '#0C1B3A' }}>nexwave</span>
                    </div>
                    <div style={{ flex: 1 }} />
                    {!isViewerOnly && <NotificationBell />}
                </div>
                <div
                    className="main-body"
                    ref={bodyRef}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                >
                    {/* Pull-to-refresh indicator (home-screen app) */}
                    {pullDist > 0 && (
                        <div style={{
                            height: pullDist, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#64748B', fontFamily: "'Poppins', sans-serif", fontSize: '0.75rem',
                            transition: pull.current.active ? 'none' : 'height 0.2s',
                        }}>
                            {pullDist >= REFRESH_THRESHOLD ? 'Release to refresh' : 'Pull to refresh'}
                        </div>
                    )}
                    {profileIncomplete && location.pathname === '/user/profile' && (
                        <div style={{
                            background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 10,
                            padding: '12px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10,
                            fontFamily: "'Poppins', sans-serif"
                        }}>
                            <span style={{ fontSize: 20 }}>⚠️</span>
                            <div>
                                <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#92400E' }}>Please complete your profile</div>
                                <div style={{ fontSize: '0.75rem', color: '#A16207' }}>
                                    Fill in all fields below for billing and accounting purposes. You need to complete your profile before accessing other pages.
                                </div>
                            </div>
                        </div>
                    )}
                    {children}
                </div>
            </div>
        </div>
    );
};

export default MainLayout;
